import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box, Typography, Tabs, Tab, Paper, Grid, TextField,
    Button, CircularProgress, Alert, Snackbar, InputAdornment,
    Chip, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadDropzone from '../../components/FileUploadDropzone';
import { HotelService } from '../../api/services/hotelService';
import { SettingsService } from '../../api/services/settingsService';
import CancellationPoliciesPage from './CancellationPoliciesPage';
import { setActiveCurrency } from '../../utils/currency';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function HotelSettings() {
    const dispatch = useDispatch();
    const [tabValue, setTabValue] = useState(0);
    const [settings, setSettings] = useState({
        branding: { name: '', logo: '', tagline: '', primaryColor: '', secondaryColor: '' },
        contact: { address: '', phone: '', email: '', website: '', city: '', state: '', country: '', postalCode: '' },
        localization: { timezone: '', currency: '', language: 'en' },
        policies: { checkInTime: '14:00', checkOutTime: '11:00', depositPolicy: '', cancellationPolicy: '' }
    });
    const [loading, setLoading] = useState(false);
    const [brandingLoading, setBrandingLoading] = useState(false);
    const [localizationLoading, setLocalizationLoading] = useState(false);
    const [policiesLoading, setPoliciesLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const { tenants } = useSelector((state) => state.auth);

    const fetchInitialSettings = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const tenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
            // Find the actual hotel ID from Redux state since tenantId is just the number (e.g. "7")
            const activeTenant = tenants.find(t => String(t.tenantId || t.id || t._id) === String(tenantId));
            const hotelId = activeTenant?.id || activeTenant?._id;

            // Fetch from Settings Branding API (preferred source for tenant branding)
            let brandingData = null;
            try {
                const brandingRes = await SettingsService.getBranding(tenantId);
                brandingData = brandingRes?.data || brandingRes;
            } catch (err) {
                console.warn("Could not fetch settings branding:", err);
            }

            // Fetch hotel profile data
            let hotelData = null;
            if (hotelId) {
                try {
                    const response = await HotelService.getById(hotelId, tenantId);
                    hotelData = response?.data || response;
                } catch (err) {
                    console.warn("Could not fetch hotel data:", err);
                }
            }

            const data = { ...(hotelData || {}), ...(brandingData || {}) };

            if (data && (Object.keys(data).length > 0)) {
                if (data.currency) {
                    setActiveCurrency(data.currency);
                }
                setSettings(prev => ({
                    ...prev,
                    branding: {
                        ...prev.branding,
                        name: data.appName || data.name || '',
                        logo: data.logoUrl || data.logo || '',
                        primaryColor: data.primaryColor || '',
                        secondaryColor: data.secondaryColor || '',
                        tagline: data.tagline || ''
                    },
                    contact: {
                        ...prev.contact,
                        address: data.address || '',
                        phone: data.phone || '',
                        email: data.email || '',
                        website: data.website || '',
                        city: data.city || '',
                        state: data.state || '',
                        country: data.country || '',
                        postalCode: data.postalCode || ''
                    },
                    localization: {
                        ...prev.localization,
                        timezone: data.timezone || '',
                        currency: data.currency || '',
                        language: data.language || 'en'
                    },
                        policies: {
                            ...prev.policies,
                            checkInTime: data.checkInTime || '14:00',
                            checkOutTime: data.checkOutTime || '11:00',
                            depositPolicy: data.depositPolicy || '',
                            cancellationPolicy: data.cancellationPolicy || ''
                        }
                    }));
                }
            } catch (err) {
                console.error('Failed to fetch initial hotel settings:', err);
                setNotification({ open: true, message: 'Failed to load existing profile data.', severity: 'warning' });
            } finally {
                if (showLoading) setLoading(false);
            }
    };

    React.useEffect(() => {
        if (tenants && tenants.length > 0) {
            fetchInitialSettings();
        }
    }, [tenants]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const removeLogo = () => {
        setSettings(prev => ({
            ...prev,
            branding: { ...prev.branding, logo: '' }
        }));
    };

    const handleChange = (section, field, value) => {
        if (section === 'contact' && field === 'phone') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleSaveBranding = async () => {
        if (!settings.branding.name?.trim()) {
            setNotification({ open: true, message: 'Hotel name is required', severity: 'error' });
            return;
        }
        if (settings.branding.name.length > 50) {
            setNotification({ open: true, message: 'Hotel name cannot exceed 50 characters', severity: 'error' });
            return;
        }
        if (settings.branding.tagline?.trim() && settings.branding.tagline.trim().length > 100) {
            setNotification({ open: true, message: 'Tagline cannot exceed 100 characters', severity: 'error' });
            return;
        }
        if (!settings.contact.address?.trim()) {
            setNotification({ open: true, message: 'Street address is required', severity: 'error' });
            return;
        }
        if (!settings.contact.city?.trim()) {
            setNotification({ open: true, message: 'City is required', severity: 'error' });
            return;
        }
        if (!settings.contact.country?.trim()) {
            setNotification({ open: true, message: 'Country is required', severity: 'error' });
            return;
        }
        if (!settings.contact.postalCode?.trim()) {
            setNotification({ open: true, message: 'Postal code is required', severity: 'error' });
            return;
        }
        if (!/^[a-zA-Z0-9\s-]{3,10}$/.test(settings.contact.postalCode.trim())) {
            setNotification({ open: true, message: 'Invalid postal code format (3-10 alphanumeric characters)', severity: 'error' });
            return;
        }
        if (settings.branding.primaryColor?.trim() && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(settings.branding.primaryColor.trim())) {
            setNotification({ open: true, message: 'Invalid primary color hex format (e.g. #1976d2)', severity: 'error' });
            return;
        }
        if (settings.branding.secondaryColor?.trim() && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(settings.branding.secondaryColor.trim())) {
            setNotification({ open: true, message: 'Invalid secondary color hex format (e.g. #202638)', severity: 'error' });
            return;
        }
        if (!settings.contact.phone?.trim()) {
            setNotification({ open: true, message: 'Phone number is required', severity: 'error' });
            return;
        }
        if (settings.contact.phone.length !== 10) {
            setNotification({ open: true, message: 'Phone number must be exactly 10 digits', severity: 'error' });
            return;
        }
        if (!settings.contact.email?.trim()) {
            setNotification({ open: true, message: 'Email address is required', severity: 'error' });
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.contact.email.trim())) {
            setNotification({ open: true, message: 'Invalid email address', severity: 'error' });
            return;
        }
        if (settings.contact.website?.trim() && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(settings.contact.website.trim())) {
            setNotification({ open: true, message: 'Invalid website URL (must start with http:// or https://)', severity: 'error' });
            return;
        }
        setBrandingLoading(true);
        try {
            const tenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
            const sanitizedBranding = {};
            Object.keys(settings.branding).forEach(key => {
                let val = settings.branding[key];
                let payloadKey = key === 'logo' ? 'logoUrl' : key;
                if (typeof val === 'string') {
                    val = val.trim().replace(/<\/?[^>]+(>|$)/g, "");
                    if (val !== "") {
                        sanitizedBranding[payloadKey] = val;
                        if (key === 'name') sanitizedBranding['appName'] = val;
                    }
                } else if (val !== null && val !== undefined && val !== "") {
                    sanitizedBranding[payloadKey] = val;
                    if (key === 'name') sanitizedBranding['appName'] = val;
                }
            });

            const sanitizedContact = {};
            Object.keys(settings.contact).forEach(key => {
                let val = settings.contact[key];
                if (typeof val === 'string') {
                    val = val.trim().replace(/<\/?[^>]+(>|$)/g, "");
                    if (val !== "") sanitizedContact[key] = val;
                } else if (val !== null && val !== undefined && val !== "") {
                    sanitizedContact[key] = val;
                }
            });

            const payload = {
                ...sanitizedBranding,
                ...sanitizedContact
            };
            await SettingsService.updateBranding(payload, tenantId);
            await fetchInitialSettings(false);
            if (dispatch.branding) {
                dispatch.branding.loadAuthenticatedBrandingAsync(tenantId);
            }
            setNotification({ open: true, message: 'Branding & Contact info updated!', severity: 'success' });
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update branding', severity: 'error' });
        } finally {
            setBrandingLoading(false);
        }
    };

    const handleDiscard = () => {
        fetchInitialSettings(false);
    };

    const handleSaveLocalization = async () => {
        setLocalizationLoading(true);
        try {
            const tenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
            const payload = {
                timezone: settings.localization.timezone,
                currency: settings.localization.currency,
                language: settings.localization.language
            };
            await SettingsService.updateLocalization(payload, tenantId);
            if (payload.currency) {
                setActiveCurrency(payload.currency);
            }
            await fetchInitialSettings(false);
            setNotification({ open: true, message: 'Localization settings updated!', severity: 'success' });
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update localization', severity: 'error' });
        } finally {
            setLocalizationLoading(false);
        }
    };

    const handleSavePolicies = async () => {
        setPoliciesLoading(true);
        try {
            const tenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
            const payload = {
                checkInTime: settings.policies.checkInTime,
                checkOutTime: settings.policies.checkOutTime,
                depositPolicy: settings.policies.depositPolicy,
                cancellationPolicy: settings.policies.cancellationPolicy
            };
            await SettingsService.updatePolicies(payload, tenantId);
            await fetchInitialSettings(false);
            setNotification({ open: true, message: 'Policies updated successfully!', severity: 'success' });
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update policies', severity: 'error' });
        } finally {
            setPoliciesLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Hotel Profile</Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            minWidth: 'auto',
                            px: 2.5,
                            py: 1.5,
                            color: '#64748b',
                            '&.Mui-selected': {
                                color: 'primary.main'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: 'primary.main',
                            height: 2.5,
                            borderRadius: '2px 2px 0 0'
                        }
                    }}
                >
                    <Tab label="Branding & Contact" />
                    <Tab label="Localization" />
                    <Tab label="Policies" />
                </Tabs>
            </Box>

            {loading && !brandingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : (
                <Box>
                    {/* Branding & Contact */}
                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3} alignItems="flex-start">
                            {/* Left Card: Hotel Branding & Identity */}
                            <Grid item xs={12} md={4.2}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: '16px',
                                        border: '1px solid #e9ecef',
                                        bgcolor: '#ffffff',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', fontSize: '1rem' }}>
                                             Hotel Branding & Identity
                                        </Typography>
                                        <Chip
                                            label="Active Asset"
                                            size="small"
                                            sx={{
                                                bgcolor: '#eff4ff',
                                                color: '#3538cd',
                                                border: '1px solid #dbe4ff',
                                                fontWeight: 600,
                                                fontSize: '0.72rem',
                                                height: 24,
                                                borderRadius: '12px'
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{
                                        bgcolor: '#f8fafc',
                                        borderRadius: '12px',
                                        py: 3.5,
                                        px: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        border: '1px solid #f1f5f9'
                                    }}>
                                        {settings.branding.logo ? (
                                            <>
                                                <Box sx={{
                                                    width: 104,
                                                    height: 104,
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: '#ffffff',
                                                    border: '2px solid #e2e8f0',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                                }}>
                                                    <img
                                                        src={settings.branding.logo}
                                                        alt="Hotel Logo"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </Box>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                                    onClick={removeLogo}
                                                    sx={{
                                                        mt: 2,
                                                        mb: 1,
                                                        color: '#d92d20',
                                                        borderColor: '#fee4e2',
                                                        bgcolor: '#ffffff',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                        borderRadius: '8px',
                                                        py: 0.6,
                                                        px: 2,
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            bgcolor: '#fef3f2',
                                                            borderColor: '#fda29b'
                                                        }
                                                    }}
                                                >
                                                    Remove Logo
                                                </Button>
                                                <Typography variant="caption" sx={{ color: '#98a2b3', fontSize: '0.72rem' }}>
                                                    PNG, SVG, or JPG (max. 2MB)
                                                </Typography>
                                            </>
                                        ) : (
                                            <Box sx={{ width: '100%' }}>
                                                <FileUploadDropzone
                                                    accept="image/png,image/jpeg"
                                                    maxSizeMB={2}
                                                    onUploadSuccess={(url) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            branding: { ...prev.branding, logo: url }
                                                        }));
                                                    }}
                                                />
                                                <Typography variant="caption" sx={{ color: '#98a2b3', fontSize: '0.72rem', display: 'block', mt: 1 }}>
                                                    PNG, SVG, or JPG (max. 2MB)
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ mt: 3 }}>
                                        <TextField
                                            fullWidth
                                            label="Tagline"
                                            value={settings.branding.tagline || ''}
                                            onChange={(e) => handleChange('branding', 'tagline', e.target.value)}
                                            placeholder="e.g. Test Hospitality Services Pvt. Ltd."
                                            inputProps={{ maxLength: 100 }}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    bgcolor: '#ffffff'
                                                }
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ mt: 2.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#344054', display: 'block', mb: 1 }}>
                                            Theme Colors
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Primary Color"
                                                    value={settings.branding.primaryColor || ''}
                                                    onChange={(e) => handleChange('branding', 'primaryColor', e.target.value)}
                                                    placeholder="#1976d2"
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Box
                                                                    component="input"
                                                                    type="color"
                                                                    value={settings.branding.primaryColor?.startsWith('#') && settings.branding.primaryColor.length === 7 ? settings.branding.primaryColor : '#1976d2'}
                                                                    onChange={(e) => handleChange('branding', 'primaryColor', e.target.value)}
                                                                    sx={{
                                                                        width: 22,
                                                                        height: 22,
                                                                        p: 0,
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        bgcolor: 'transparent',
                                                                        '&::-webkit-color-swatch-wrapper': { p: 0 },
                                                                        '&::-webkit-color-swatch': { border: '1px solid #d0d5dd', borderRadius: '4px' }
                                                                    }}
                                                                />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            bgcolor: '#ffffff'
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Secondary Color"
                                                    value={settings.branding.secondaryColor || ''}
                                                    onChange={(e) => handleChange('branding', 'secondaryColor', e.target.value)}
                                                    placeholder="#202638"
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Box
                                                                    component="input"
                                                                    type="color"
                                                                    value={settings.branding.secondaryColor?.startsWith('#') && settings.branding.secondaryColor.length === 7 ? settings.branding.secondaryColor : '#202638'}
                                                                    onChange={(e) => handleChange('branding', 'secondaryColor', e.target.value)}
                                                                    sx={{
                                                                        width: 22,
                                                                        height: 22,
                                                                        p: 0,
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        bgcolor: 'transparent',
                                                                        '&::-webkit-color-swatch-wrapper': { p: 0 },
                                                                        '&::-webkit-color-swatch': { border: '1px solid #d0d5dd', borderRadius: '4px' }
                                                                    }}
                                                                />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            bgcolor: '#ffffff'
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Right Card: Contact & Location */}
                            <Grid item xs={12} md={7.8}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3.5,
                                        borderRadius: '16px',
                                        border: '1px solid #e9ecef',
                                        bgcolor: '#ffffff'
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', fontSize: '1rem', mb: 2.5 }}>
                                        Key Contact & Web Presence
                                    </Typography>
                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Hotel Name"
                                                value={settings.branding.name || ''}
                                                onChange={(e) => handleChange('branding', 'name', e.target.value)}
                                                inputProps={{ maxLength: 50 }}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Website URL"
                                                value={settings.contact.website || ''}
                                                onChange={(e) => handleChange('contact', 'website', e.target.value)}
                                                placeholder="https://qa-hotel.example.com"
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Phone Number"
                                                value={settings.contact.phone || ''}
                                                onChange={(e) => handleChange('contact', 'phone', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Email Address"
                                                value={settings.contact.email || ''}
                                                onChange={(e) => handleChange('contact', 'email', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 3.5 }} />

                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', fontSize: '1rem', mb: 2.5 }}>
                                        Physical Location & Dispatch Address
                                    </Typography>
                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Street Address"
                                                value={settings.contact.address || ''}
                                                onChange={(e) => handleChange('contact', 'address', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="City"
                                                value={settings.contact.city || ''}
                                                onChange={(e) => handleChange('contact', 'city', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="State / Province"
                                                value={settings.contact.state || ''}
                                                onChange={(e) => handleChange('contact', 'state', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Country"
                                                value={settings.contact.country || ''}
                                                onChange={(e) => handleChange('contact', 'country', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Postal Code"
                                                value={settings.contact.postalCode || ''}
                                                onChange={(e) => handleChange('contact', 'postalCode', e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{
                                        mt: 4,
                                        pt: 3,
                                        borderTop: '1px dotted #d0d5dd',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleDiscard}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                borderRadius: '8px',
                                                borderColor: '#d0d5dd',
                                                color: '#344054',
                                                bgcolor: '#ffffff',
                                                px: 2.5,
                                                py: 1,
                                                height: 40,
                                                '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                                            }}
                                        >
                                            Discard Changes
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSaveBranding}
                                            disabled={brandingLoading}
                                            startIcon={brandingLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                borderRadius: '8px',
                                                px: 3,
                                                py: 1,
                                                height: 40,
                                            }}
                                        >
                                            Save Branding & Contact
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Localization */}
                    <TabPanel value={tabValue} index={1}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3.5,
                                borderRadius: '16px',
                                border: '1px solid #e9ecef',
                                bgcolor: '#ffffff',
                                maxWidth: 840
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" mb={2}>Regional Settings</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Timezone"
                                        value={settings.localization.timezone}
                                        onChange={(e) => handleChange('localization', 'timezone', e.target.value)}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    >
                                        <option value="">Select Timezone</option>
                                        <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                                        <option value="America/New_York">America/New_York (EST)</option>
                                        <option value="Europe/Paris">Europe/Paris (CET)</option>
                                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                                        <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                                        <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                                        <option value="UTC">UTC</option>
                                        {settings.localization.timezone && !['Asia/Dubai', 'Asia/Kolkata', 'America/New_York', 'Europe/Paris', 'Europe/London', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'UTC'].includes(settings.localization.timezone) && (
                                            <option value={settings.localization.timezone}>{settings.localization.timezone}</option>
                                        )}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Currency"
                                        value={settings.localization.currency}
                                        onChange={(e) => handleChange('localization', 'currency', e.target.value)}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    >
                                        <option value="">Select Currency</option>
                                        <option value="AED">AED - UAE Dirham</option>
                                        <option value="INR">INR - Indian Rupee (₹)</option>
                                        <option value="USD">USD - US Dollar ($)</option>
                                        <option value="EUR">EUR - Euro (€)</option>
                                        <option value="GBP">GBP - British Pound (£)</option>
                                        <option value="SAR">SAR - Saudi Riyal</option>
                                        <option value="QAR">QAR - Qatari Riyal</option>
                                        <option value="BHD">BHD - Bahraini Dinar</option>
                                        <option value="OMR">OMR - Omani Rial</option>
                                        <option value="KWD">KWD - Kuwaiti Dinar</option>
                                        {settings.localization.currency && !['AED', 'INR', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'BHD', 'OMR', 'KWD'].includes(settings.localization.currency) && (
                                            <option value={settings.localization.currency}>{settings.localization.currency}</option>
                                        )}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Language"
                                        value={settings.localization.language}
                                        onChange={(e) => handleChange('localization', 'language', e.target.value)}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    >
                                        <option value="en">English (US)</option>
                                        <option value="hi">Hindi</option>
                                        <option value="ar">Arabic</option>
                                        <option value="fr">French</option>
                                        <option value="de">German</option>
                                        <option value="es">Spanish</option>
                                        {settings.localization.language && !['en', 'hi', 'ar', 'fr', 'de', 'es'].includes(settings.localization.language) && (
                                            <option value={settings.localization.language}>{settings.localization.language}</option>
                                        )}
                                    </TextField>
                                </Grid>
                            </Grid>
                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveLocalization}
                                    disabled={localizationLoading}
                                    startIcon={localizationLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        borderRadius: '8px',
                                        px: 3,
                                        py: 1,
                                    }}
                                >
                                    Save Localization
                                </Button>
                            </Box>
                        </Paper>
                    </TabPanel>

                    {/* Policies */}
                    <TabPanel value={tabValue} index={2}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3.5,
                                borderRadius: '16px',
                                border: '1px solid #e9ecef',
                                bgcolor: '#ffffff'
                            }}
                        >
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" fontWeight="bold" mb={2}>Operational Times</Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                                        <TextField
                                            fullWidth
                                            type="time"
                                            label="Check-in Time"
                                            value={settings.policies.checkInTime}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(e) => handleChange('policies', 'checkInTime', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                        />
                                        <TextField
                                            fullWidth
                                            type="time"
                                            label="Check-out Time"
                                            value={settings.policies.checkOutTime}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(e) => handleChange('policies', 'checkOutTime', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6" fontWeight="bold" mb={2}>Booking Policies</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Deposit Policy"
                                        value={settings.policies.depositPolicy}
                                        placeholder="e.g., 50% upfront via bank transfer or card."
                                        onChange={(e) => handleChange('policies', 'depositPolicy', e.target.value)}
                                        multiline
                                        rows={3}
                                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    />
                                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSavePolicies}
                                            disabled={policiesLoading}
                                            startIcon={policiesLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                borderRadius: '8px',
                                                px: 3,
                                                py: 1,
                                            }}
                                        >
                                            Save Policies
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ mt: 2, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <CancellationPoliciesPage />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </TabPanel>
                </Box>
            )}

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
